import classNames from 'classnames'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
// components
import { Button, FormattedText, Icon, Loader, Message, Typography } from 'src/modules/core/components'
import { TypeformLiveEmbed } from 'src/modules/core/components/typeform-live-embed'
// constants
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { useUserEventStats } from 'src/modules/event/hooks/use-user-event-stats'
import {
  CHURN_SURVEY_STABLE_QUERY_OPTIONS,
  CHURN_SURVEY_TYPEFORM_LIVE_ID,
} from 'src/modules/payment/constants/churn-survey.constants'
import { useCancelSubscription } from 'src/modules/payment/hooks/use-cancel-subscription'
// hooks
import { usePlanData } from 'src/modules/payment/hooks/use-plan-data'
import { useShopifyCancelUrl } from 'src/modules/payment/hooks/use-shopify-billing'
import { useSubscription } from 'src/modules/payment/hooks/use-subscription'
// types
import { SubscriptionStatus } from 'src/modules/payment/types/payment.types'
import {
  isChurnSurveyEmbedReady,
  resolveChurnSurveyActiveEventsCount,
  resolveChurnSurveySelectedCalendarsCount,
  resolveChurnSurveyPlanName,
  resolveChurnSurveySubscriptionStartDateIso,
} from 'src/modules/payment/utils/churn-survey-embed-ready'
import { buildChurnSurveyHiddenFields } from 'src/modules/payment/utils/churn-survey-hidden-fields'
import { isShopifyBilledProfile } from 'src/modules/payment/utils/helpers'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import classes from './cancel-plan-wrapper.module.scss'

import type { MessageType } from './cancel-plan-wrapper.interface'
export const CancelPlanWrapper: FC = () => {
  // state
  const [message, setMessage] = useState<MessageType>(null)
  // hooks
  const navigate = useNavigate()
  const profile = useProfile({}, CHURN_SURVEY_STABLE_QUERY_OPTIONS)
  const isShopifyBilled = isShopifyBilledProfile(profile.data)
  const subscriptionOptions = {
    enabled: Boolean(profile.data?.billing_subscription_id) || isShopifyBilled,
    ...CHURN_SURVEY_STABLE_QUERY_OPTIONS,
  }
  const subscriptionPlansOptions = CHURN_SURVEY_STABLE_QUERY_OPTIONS
  const subscription = useSubscription({}, subscriptionOptions)
  const planData = usePlanData({
    subscriptionOptions,
    subscriptionPlansOptions,
    profileOptions: CHURN_SURVEY_STABLE_QUERY_OPTIONS,
    freePlanOptions: CHURN_SURVEY_STABLE_QUERY_OPTIONS,
  })
  // Hidden fields are read by Typeform only at embed init; keep query data stable for the session.
  const calendars = useCalendars(undefined, { sync: false, ...CHURN_SURVEY_STABLE_QUERY_OPTIONS })
  const eventStats = useUserEventStats(CHURN_SURVEY_STABLE_QUERY_OPTIONS)
  const cancelSubscription = useCancelSubscription()
  const shopifyCancelUrl = useShopifyCancelUrl({ enabled: isShopifyBilled })

  const subscriptionEnabled = subscriptionOptions.enabled
  const activeEventsCount = resolveChurnSurveyActiveEventsCount(eventStats)
  const selectedCalendarsCount = resolveChurnSurveySelectedCalendarsCount(calendars)
  const churnSurveyReady = isChurnSurveyEmbedReady({
    profile: profile.data,
    planLoading: planData.loading,
    selectedCalendarsCount,
    activeEventsCount,
  })

  const churnSurveyHiddenFields = useMemo(() => {
    if (!profile.data || activeEventsCount === null || selectedCalendarsCount === null) {
      return undefined
    }

    const planName = resolveChurnSurveyPlanName({
      subscriptionEnabled,
      subscriptionQuery: subscription,
      subscriptionPlansQuery: planData.subscriptionPlans,
      currentPlanName: planData.currentPlan?.name,
    })
    const subscriptionStartDateIso = resolveChurnSurveySubscriptionStartDateIso({
      subscriptionEnabled,
      subscriptionQuery: subscription,
      profileCreatedAt: profile.data.created_at,
    })

    return buildChurnSurveyHiddenFields({
      profile: profile.data,
      planName,
      subscriptionStartDateIso,
      activeEventsCount,
      selectedCalendarsCount,
    })
  }, [
    activeEventsCount,
    selectedCalendarsCount,
    planData.currentPlan?.name,
    planData.subscriptionPlans,
    profile.data,
    subscription,
    subscriptionEnabled,
  ])

  useEffect(() => {
    if (subscription.data?.state === SubscriptionStatus.deleted && !message) {
      navigate(routeNames.dashboard.path, { replace: true })
    }
  }, [message, navigate, subscription.data?.state])

  const onClose = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const handleCancelPlan = useCallback(() => {
    if (isShopifyBilled) {
      // Embedded app runs `appSubscriptionCancel`; monolith provides
      // `?intent=cancel` deep link into Shopify Admin.
      const url = shopifyCancelUrl.data?.shopify_admin_url
      if (url) {
        window.top?.location.assign(url)
      }
      return
    }
    cancelSubscription.mutate(null, {
      onSuccess: () => {
        setMessage({
          status: 'success',
          text: <FormattedText id="payment.subscription.cancel.success" />,
        })
      },
      onError: () => {
        setMessage({
          status: 'error',
          text: <FormattedText id="payment.subscription.cancel.error" />,
        })
      },
    })
  }, [cancelSubscription, isShopifyBilled, shopifyCancelUrl.data?.shopify_admin_url])

  return (
    <div className={classes.root}>
      <Loader isLoading={cancelSubscription.isLoading} className={classes.loader} full />
      {message ? (
        <Message variant={message.status} message={message.text} onClose={onClose} />
      ) : (
        <>
          <div className={classes.header}>
            <Typography variant="h1" className={classes.title}>
              <FormattedText id="payment.subscription.cancel.title" />
            </Typography>
            <Icon name="close" className={classes.close} onClick={onClose} />
          </div>
          <Typography variant="body">
            <FormattedText id="payment.subscription.cancel.description" />
          </Typography>
          <TypeformLiveEmbed
            className={classNames('my-form', classes.form)}
            formId={CHURN_SURVEY_TYPEFORM_LIVE_ID}
            hiddenFields={churnSurveyHiddenFields}
            ready={churnSurveyReady}
          />
          <div className={classes.buttonWrapper}>
            <Button
              variant="danger"
              className={classes.button}
              onClick={handleCancelPlan}
              disabled={
                (isShopifyBilled && !shopifyCancelUrl.data) || (!isShopifyBilled && cancelSubscription.isLoading)
              }
            >
              <FormattedText
                id={
                  isShopifyBilled ? 'payment.subscription.change.cancel.shopify' : 'payment.subscription.cancel.button'
                }
              />
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
